/**
 * W-17 收益面板(SELLER)。视觉真源 design/earnings.html。
 * summary + history 两独立 query。结算 5%/95、T+N 为真实记账只读聚合(不重算、不 mock)。
 * FR-12=B:顶部 CTA 由「去提现」改为「收款账户」→ /withdraw-account(无主动提现)。
 */
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getEarningsSummary, type PendingSettlementItem } from '@/api/wallet';
import { formatYuan } from '@/lib/formatMoney';
import { useSettleCountdown } from '@/lib/settleCountdown';
import { PayoutHistory } from '@/components/wallet/PayoutHistory';

export function EarningsPage() {
  const summaryQuery = useQuery({
    queryKey: ['earnings', 'summary'],
    queryFn: getEarningsSummary,
  });

  if (summaryQuery.isLoading) {
    return (
      <div className="inner section">
        <div className="state">
          <div className="skeleton" style={{ width: 240, height: 24 }} />
          <p className="muted sm mt-3">加载收益中…</p>
        </div>
      </div>
    );
  }

  if (summaryQuery.isError || !summaryQuery.data) {
    return (
      <div className="inner section">
        <div className="state">
          <div className="ic">⚠️</div>
          <h2 className="h3 mb-3">网络异常,请稍后重试</h2>
          <button type="button" className="btn btn-secondary" onClick={() => summaryQuery.refetch()}>
            点此重试
          </button>
        </div>
      </div>
    );
  }

  const s = summaryQuery.data;
  const pendingItems = s.pendingItems ?? [];
  const isEmpty =
    !s.cumulativeAmount && !s.currentMonthAmount && !(s.pendingCount ?? 0);

  return (
    <div className="inner section">
      <div className="sm muted mb-6">
        <Link to="/">首页</Link> / <Link to="/me">个人中心</Link> / 我的收益
      </div>

      <div className="row between mb-6">
        <h1 className="h2">我的收益</h1>
        <Link to="/withdraw-account" className="btn btn-primary">
          收款账户
        </Link>
      </div>

      {isEmpty ? (
        <div className="state">
          <div className="ic">💰</div>
          <h2 className="h3 mb-3">暂无收益记录</h2>
          <p className="muted sm">完成一单结算后,收益将在此显示。</p>
        </div>
      ) : (
        <>
          {/* 收益汇总卡:三栏 */}
          <section className="row gap-6 mb-8" style={{ alignItems: 'stretch' }}>
            <div className="card card-pad" style={{ flex: 1 }}>
              <div className="sm muted">累计到账</div>
              <div className="mt-2" style={{ fontSize: 'var(--text-4xl-size)', fontWeight: 'var(--weight-bold)' }}>
                {formatYuan(s.cumulativeAmount)}
              </div>
              <div className="xs muted mt-4">交易完成结算后到账金额合计</div>
            </div>
            <div className="card card-pad" style={{ flex: 1 }}>
              <div className="sm muted">本月到账</div>
              <div className="mt-2" style={{ fontSize: 'var(--text-4xl-size)', fontWeight: 'var(--weight-bold)' }}>
                {formatYuan(s.currentMonthAmount)}
              </div>
              <div className="xs muted mt-4">本月(Asia/Shanghai)已结算到账</div>
            </div>
            <div className="card card-pad" style={{ flex: 1 }}>
              <div className="sm muted">待结算</div>
              <div className="mt-2" style={{ fontSize: 'var(--text-4xl-size)', fontWeight: 'var(--weight-bold)' }}>
                {formatYuan(s.pendingAmount)}
              </div>
              <div className="xs muted mt-4">{s.pendingCount ?? 0} 笔订单已完成,结算中</div>
            </div>
          </section>

          {/* 待打款分区(pendingCount>0 才渲染) */}
          {(s.pendingCount ?? 0) > 0 && (
            <section className="mb-8">
              <h3 className="h3 mb-4">
                待打款 · {s.pendingCount} 笔 · 共 {formatYuan(s.pendingAmount)}
              </h3>
              <div className="order-list">
                {pendingItems.map((item, idx) => (
                  <PendingRow key={item.orderId || item.productId || idx} item={item} />
                ))}
              </div>
            </section>
          )}

          {/* 已打款历史 */}
          <PayoutHistory title="已打款历史" emptyText="暂无已打款记录" />

          <p className="hint mt-4">
            平台就每笔成功完成的交易收取成交金额 5% 作为技术服务费,结算时自动扣除,卖家到账 95%。到账为
            T+7 自动结算,无需手动提现。
          </p>
        </>
      )}
    </div>
  );
}

function PendingRow({ item }: { item: PendingSettlementItem }) {
  const navigate = useNavigate();
  const cd = useSettleCountdown(item.settleDeadlineAt);
  // 打款倒计时语气:逾期/今日打款=红(提示),常规=灰(与订单列表状态点统一)。
  const statusClass = cd.overdue
    ? 'order-status order-status--attention'
    : cd.dueToday
      ? 'order-status order-status--pay'
      : 'order-status order-status--muted';
  const open = () => item.orderId && navigate(`/orders/${item.orderId}`);
  return (
    <div
      className="order-card"
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      <div className="order-card__head">
        <span className="order-card__meta">交易已完成 · 结算中</span>
        <span className={statusClass}>{cd.label}</span>
      </div>
      <div className="order-card__body">
        <div className="order-card__cover">
          {item.coverImageUrl ? (
            <img src={item.coverImageUrl} alt="" />
          ) : item.productTitle ? (
            item.productTitle[0]
          ) : (
            '单'
          )}
        </div>
        <div className="order-card__info">
          <span className="order-card__title">{item.productTitle || '商品已下架'}</span>
          <span className="xs muted">
            我将到账{' '}
            <span style={{ color: 'var(--color-primary)', fontWeight: 'var(--weight-semibold)' }}>
              {formatYuan(item.sellerAmount)}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
